import { AXIS_STYLE, MAIN_STYLE, MAIN_STYLE_GROUPS, PADDING_SIDES, PRESS_EVENTS } from '../shared.js'
export const chart = {

    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                padding:          { type: 'int',   min: 0, exportSpecial: true },
                ...PADDING_SIDES,
                grid_color:       { type: 'color', label: 'grid color' },
                // Real, wired firmware attributes (ATTR_GRID_DASH_WIDTH/ATTR_GRID_DASH_GAP,
                // hasp_chart.h) — firmware overrides the theme's dashed-grid default to solid
                // at chart creation, so 0 (solid) is the correct default here, matching that
                // creation-time override rather than the theme's own dashed default.
                grid_dash_width:  { type: 'int',   min: 0, label: 'grid dash width' },
                grid_dash_gap:    { type: 'int',   min: 0, label: 'grid dash gap' },
                hdiv:             { type: 'int',   min: 0, label: 'horiz divisions' },
                vdiv:             { type: 'int',   min: 0, label: 'vert divisions' },
                line_width:       { type: 'int',   min: 0, label: 'line width' },
                point_size:       { type: 'int',   min: 0, label: 'point size' },
                type: {
                    type: 'enum',
                    label: 'chart type',
                    values: [
                        { value: 0, label: 'none' },
                        { value: 1, label: 'line' },
                        { value: 2, label: 'column' },
                    ],
                },
                update_mode: {
                    type: 'enum',
                    label: 'update mode',
                    values: [
                        { value: 0, label: 'shift' },
                        { value: 1, label: 'circular' },
                    ],
                },
            },
        },
        // Axis appearance (label text + ticks) — X and Y scales style
        // independently via the shared AXIS_STYLE (shared.js).
        //
        // exportSuffix values follow the standard OpenHASP part-postfix
        // convention (multiples of 10, see OPENHASP_PART_SUFFIX in
        // shared.js) rather than inventing new digit widths. No LVGL/
        // OpenHASP part exists for "a chart's second/third axis" specifically
        // (firmware doesn't route chart's scale attributes through part
        // suffixes at all today — see x_scale/y_scale's own wireKey comments
        // below), so exact concept-matching isn't possible for x_axis, but a
        // reasonable pair was chosen from the documented part semantics:
        //   x_axis → 60 (major_ticks' slot) — the closest available semantic
        //            parallel, since an axis IS fundamentally tick/scale styling.
        //   y_axis → 90 (special's slot) — 'special' is this table's own
        //            designated catch-all for a part that doesn't fit any of
        //            the other named LVGL concepts, which is exactly the
        //            second/independent-axis case here. Not text_cursor (70):
        //            a Y axis has no conceptual relation to a text-input caret.
        // Per the Designer-first policy for Chart (see AXIS_STYLE's own
        // comment in shared.js), firmware is expected to adopt this numbering
        // later, not the other way around.
        x_axis: {
            label: 'X Axis',
            exportSuffix: '60',
            order: 1,
            properties: {
                // Flat, unsuffixed firmware attribute (ATTR_X_SCALE, hasp_chart.h) —
                // wireKey pins its exported/imported name to `x_scale` regardless
                // of this part's own exportSuffix (see wireFormat.js's toWireKey).
                x_scale: { type: 'bool', label: 'Show Axis', wireKey: 'x_scale' },
                ...AXIS_STYLE,
            },
        },
        y_axis: {
            label: 'Y Axis',
            exportSuffix: '90',
            order: 2,
            properties: {
                // See x_axis.x_scale above — ATTR_Y_SCALE, same rationale.
                y_scale: { type: 'bool', label: 'Show Axis', wireKey: 'y_scale' },
                // Flat firmware attribute. This is a concrete chart default;
                // zero is a valid value and is never treated as "unset".
                y_axis_decimals: { type: 'int', min: 0, max: 6, label: 'Y-axis decimals', wireKey: 'y_axis_decimals' },
                ...AXIS_STYLE,
            },
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width:      { type: 'int', min: 0 }, height:     { type: 'int', min: 0 },
        min_width:  { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid:   { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        // Chart-specific
        series:      { type: 'array',  label: 'series colors (JSON)' },
        point_count: { type: 'int',    min: 1,     label: 'point count' },
        // Firmware hard-clamps decimals to 0-2 internally (hasp_chart.cpp:264); values above 2
        // are silently clamped on-device, so max is capped here to match.
        decimals:    { type: 'int',    min: 0, max: 2, label: 'decimal places' },
        min:         { type: 'float',  step: 0.1,  label: 'Y min' },
        max:         { type: 'float',  step: 0.1,  label: 'Y max' },
        auto_range:  { type: 'bool',   label: 'auto range from data' },
        dataset:     { type: 'resource', resourceType: 'dataset', label: 'Dataset', internal: true },
        entityid:    { type: 'string', internal: true },
        stretch_mode: {
            type: 'string', internal: true, default: 'full',
            options: ['none', 'v_stretch', 'h_stretch', 'full'],
        },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 300, height: 150, min_width: 60, min_height: 30,
            enabled: true, hidden: false,
            series: ['#ff4444'],
            point_count: 10,
            decimals: 0,
            min: 0, max: 100, auto_range: false,
        },
        styles: {
            main: {
                // LVGL pad_small is about 6 px at the Designer's preview DPI.
                type: 1, update_mode: 0, padding: 6,
                grid_color: '#808080',
                // 0 = solid, matching firmware's creation-time override of the theme's
                // dashed-grid default (see grid_dash_width/grid_dash_gap property comments above).
                grid_dash_width: 0, grid_dash_gap: 0,
                hdiv: 3, vdiv: 5,
                // lv_theme_hasp.c:669 — never overridden at chart creation.
                line_width: 3, point_size: 0,
            },
            // text_font: 26 — HaspChart.js's drawChart() always renders axis
            // labels at 26px whenever text_font resolves to 0 (its "auto"
            // sentinel); the schema default is set to that actual rendered
            // value directly, per the Designer-first policy for Chart, so
            // the Property Editor shows what's really drawn rather than a
            // misleading "0". (0 remains a valid, explicit "use auto sizing"
            // override — the renderer still honors it — it's simply no
            // longer the default.)
            //
            // major_tick_size: 10 / minor_tick_size: 5 — fixed API defaults,
            // identical for x_axis and y_axis. These are NOT derived from this
            // schema's default chart width/height (an earlier pass computed
            // 10/20 that way — axis-orientation-dependent and tied to a
            // specific chart size, not a property of the chart's default
            // style); a chart's default tick length shouldn't depend on which
            // axis it is or how big the chart happens to be. 10/5 is a 2:1
            // major:minor ratio — minor ticks visibly shorter, matching how
            // LVGL itself distinguishes them (lv_chart.c's minor_tick_len
            // defaults to a fraction of major_tick_len when both are AUTO).
            // 0 remains available as an explicit override on either property
            // to opt into the old per-resize auto-computed behavior (see
            // AXIS_STYLE's major_tick_size comment in shared.js) — resolving
            // to this fixed default instead means an unmodified chart's ticks
            // no longer auto-scale when the chart is resized; they stay
            // pinned at 10/5 like any other explicit style value.
            //
            // major_tick_width: 1 — matches the tick line's previous hardcoded
            // Konva strokeWidth, now a real, editable property.
            //
            // minor_tick_color/minor_tick_width: identical to major_tick_*
            // (only minor_tick_size differs, deliberately) — sensible visual
            // values now that minor ticks are actually rendered.
            //
            // minor_tick_count: 4 — divides each major-to-major gap into 4
            // equal intervals (3 minor ticks between each pair of major
            // ticks), a common, unobtrusive subdivision.
            //
            // label_gap: 2 — matches drawChart()'s previous hardcoded
            // tick-to-label gap constant, now a real, per-axis, editable
            // property; identical for both axes as a starting point.
            x_axis: {
                x_scale: false, text_color: '#c8c8c8', text_font: 26, font: '',
                major_tick_color: '#ffffff', major_tick_width: 1, major_tick_size: 10,
                minor_tick_color: '#ffffff', minor_tick_width: 1, minor_tick_size: 5,
                minor_tick_count: 4, label_gap: 2,
            },
            y_axis: {
                y_scale: false, y_axis_decimals: 1, text_color: '#c8c8c8', text_font: 26, font: '',
                major_tick_color: '#ffffff', major_tick_width: 1, major_tick_size: 10,
                minor_tick_color: '#ffffff', minor_tick_width: 1, minor_tick_size: 5,
                minor_tick_count: 4, label_gap: 2,
            },
        },
    },
    states: [],
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu'],
    // Chart is genuinely clickable/action-capable in firmware — created with
    // generic_event_handler, same as buttons (hasp_object.cpp).
    runtimeCapabilities: ['has_data', 'has_action'],

    events: PRESS_EVENTS,

    bindings: {
        properties:   { min: {}, max: {}, type: {}, point_count: {}, decimals: {} },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'automation', 'esphome'] },
        events:  {},
    },
}
